/* @flow */

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { get, keyBy, toString } from 'lodash/fp';

import FlexiSelect from 'app/components/commons/FlexiSelect';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import InputField from 'app/components/commons/InputField';
import Divider from 'app/components/commons/Divider';
import { loadProjections, saveProjection } from 'app/actions/fantasyInsiderActions';
import { updatePlayers } from 'app/actions/challengeActions';
import entitiesSelector from 'app/selectors/entitiesSelector';
import Button from 'app/components/commons/Button';
import Columns from 'app/components/commons/Columns';
import Column from 'app/components/commons/Column';
import styled from '@emotion/styled';

type Props = {
  isSaving?: boolean,
  slateId: string | number,
  entities?: Array<Object>,
  projections?: Array<Object>,
  onLoadProjections: (slateId: number | string) => void,
  onSaveProjection: () => void,
  onUpdatePlayers: (players: Object) => void
};

const defaultProps = {
  isSaving: false,
  entities: [],
  projections: []
};

const StyledFlexiSelect = styled(FlexiSelect)(({ theme }) => ({
  marginBottom: theme.spacing.small
}));

const SaveLoadProjectionContent = ({
  slateId,
  isSaving,
  entities,
  onLoadProjections,
  onSaveProjection,
  onUpdatePlayers,
  projections
}: Props) => {
  const [newProjectionName, setNewProjectionName] = useState(undefined);
  const [selectedProjection, setSelectedProjection] = useState(undefined);
  const [isInitialised, setIsInitialised] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleSave = () => {
    onSaveProjection({
      id: get(
        'id',
        projections.find(p => p.name === newProjectionName)
      ),
      slate_id: slateId,
      name: newProjectionName,
      players: entities.map(({ id, idStr, insiderScoreUpdated: projection }) => ({
        id,
        idStr,
        projection
      }))
    });
  };

  const handleLoad = () => {
    if (selectedProjection) {
      const projection = projections.find(
        p => p.slateId === slateId && p.id === selectedProjection.id
      );
      onUpdatePlayers(
        keyBy(
          'idStr',
          projection.players.map(({ idStr, projection: insiderScoreUpdated }) => ({
            idStr,
            insiderScoreUpdated
          }))
        )
      );
      setLoaded(true);
    }
  };

  const handleLoadProjections = () => {
    onLoadProjections(slateId);
  };

  const init = () => {
    handleLoadProjections();
    setIsInitialised(true);
  };

  const handleRefreshProjections = () => {
    if (isInitialised && !isSaving) {
      handleLoadProjections();
      setNewProjectionName('');
    }
  };

  const handleSelectProjection = val => {
    setSelectedProjection(
      projections.find(p => p.slateId === slateId && toString(p.id) === toString(val))
    );
    setLoaded(false);
  };

  useEffect(init, []);
  useEffect(handleRefreshProjections, [isSaving]);

  return (
    <div>
      <Columns>
        <Column className="is-half">
          <h3>Save</h3>
          <p>The current projection set will be saved.</p>
          <p>Name projection set</p>
          <InputField
            autoBorderRadius={false}
            type="text"
            value={newProjectionName}
            size="small"
            onChange={val => setNewProjectionName(val)}
            inputStyle={{ height: 34 }}
            lightContent
          />
          <Button kind="success" onClick={handleSave} disabled={!newProjectionName}>
            Save
          </Button>
        </Column>
        <Column className="is-narrow">
          <Divider vertical />
        </Column>
        <Column className="is-half">
          <h3>Load</h3>
          <StyledFlexiSelect
            loading={false}
            onChange={handleSelectProjection}
            options={projections.map(({ id, name }) => ({ value: id, label: name }))}
            placeholder="Select Projection Set"
            value={get('id', selectedProjection)}
          />
          <Button kind="success" onClick={handleLoad} disabled={!selectedProjection || loaded}>
            Load
          </Button>
        </Column>
      </Columns>
      <NotificationHandler afterCond={!isSaving} beforeCond={isSaving} type="success">
        <span>
          Saved successfully
          <br />
        </span>
      </NotificationHandler>
      <NotificationHandler afterCond={loaded} beforeCond={!loaded} type="success">
        <span>
          Loaded successfully
          <br />
        </span>
      </NotificationHandler>
    </div>
  );
};

SaveLoadProjectionContent.defaultProps = defaultProps;

function mapStateToProps(state) {
  const entities = entitiesSelector(state, { isCruncher: true });
  return {
    isSaving: state.fantasyInsider.isSaving,
    entities: entities.filter(e => !!e.insiderId),
    projections: get('fantasyInsider.projections', state) || []
  };
}

const actions = {
  onLoadProjections: loadProjections,
  onSaveProjection: saveProjection,
  onUpdatePlayers: updatePlayers
};

export default connect(mapStateToProps, actions)(SaveLoadProjectionContent);
